import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowRight, ShieldCheck } from "lucide-react";
import { api, apiErrorMessage } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import EkgLine from "@/components/EkgLine";

export default function OnboardingInvite() {
    const { token } = useParams();
    const navigate = useNavigate();
    const { fetchMe } = useAuth();

    const [info, setInfo] = useState(null);
    const [err, setErr] = useState("");
    const [pw, setPw] = useState("");
    const [pw2, setPw2] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        api.get(`/onboarding/invite/${token}`)
            .then(({ data }) => setInfo(data))
            .catch((e) => setErr(apiErrorMessage(e, "We couldn't find that invitation.")));
    }, [token]);

    const submit = async (e) => {
        e.preventDefault();
        setErr("");
        if (pw.length < 8) {
            setErr("Password must be at least 8 characters.");
            return;
        }
        if (pw !== pw2) {
            setErr("Passwords do not match.");
            return;
        }
        setBusy(true);
        try {
            await api.post(`/onboarding/set-password`, { token, password: pw });
            await fetchMe();
            navigate("/onboarding", { replace: true });
        } catch (e) {
            setErr(apiErrorMessage(e));
        } finally {
            setBusy(false);
        }
    };

    if (err && !info) {
        return (
            <div
                className="min-h-screen flex items-center justify-center px-5"
                style={{ background: "var(--brand-cream)" }}
            >
                <div className="brand-card max-w-[420px] text-center">
                    <h1 className="h2">Invitation unavailable</h1>
                    <p className="text-muted mt-3">{err}</p>
                </div>
            </div>
        );
    }

    return (
        <div
            className="min-h-screen flex"
            style={{ background: "var(--brand-cream)" }}
            data-testid="onboarding-invite"
        >
            <div
                className="hidden lg:flex w-[42%] relative flex-col justify-between p-12 text-white"
                style={{ background: "var(--brand-dark)" }}
            >
                <EkgLine className="absolute top-0 left-0 right-0" />
                <div className="logo-plate" style={{ width: "fit-content" }}>
                    <img src="/brand/logo-512.png" alt="CPR Paint" style={{ height: 52 }} />
                </div>
                <div>
                    <p className="eyebrow" style={{ color: "#d6d2c9" }}>
                        Welcome aboard
                    </p>
                    <h1
                        className="font-display"
                        style={{
                            fontSize: "clamp(2rem,3.5vw,2.5rem)",
                            fontWeight: 800,
                            letterSpacing: "-0.02em",
                            lineHeight: 1.05,
                            marginTop: "1rem",
                            color: "#fff",
                        }}
                    >
                        Let's get you <span style={{ color: "var(--brand-red)" }}>on the crew</span>.
                    </h1>
                    <p style={{ marginTop: "1rem", color: "#d6d2c9", lineHeight: 1.6 }}>
                        Takes about 10–15 minutes. Have your tax forms and bank info ready.
                    </p>
                </div>
                <EkgLine className="absolute bottom-0 left-0 right-0" />
            </div>

            <div className="flex-1 flex items-center justify-center px-5 py-12">
                <div className="w-full max-w-[420px] fade-up">
                    <div className="lg:hidden mb-8 flex justify-center">
                        <div className="logo-plate">
                            <img src="/brand/logo-512.png" alt="CPR Paint" style={{ height: 44 }} />
                        </div>
                    </div>
                    <p className="eyebrow">Set your password</p>
                    <h2 className="h1 mt-3" data-testid="onboarding-invite-title">
                        Welcome, {info?.user?.name?.split(" ")[0] || "there"}.
                    </h2>
                    <p className="text-muted mt-3">
                        Create a password and we'll walk you through onboarding. Your invitation
                        is good for 7 days.
                    </p>

                    <form className="mt-8 flex flex-col gap-5" onSubmit={submit}>
                        <div>
                            <label className="field-label">Password (min 8 characters)</label>
                            <input
                                type="password"
                                className="field-input"
                                value={pw}
                                required
                                onChange={(e) => setPw(e.target.value)}
                                data-testid="invite-password"
                            />
                        </div>
                        <div>
                            <label className="field-label">Confirm password</label>
                            <input
                                type="password"
                                className="field-input"
                                value={pw2}
                                required
                                onChange={(e) => setPw2(e.target.value)}
                                data-testid="invite-password-confirm"
                            />
                        </div>
                        {err && (
                            <p className="field-error" data-testid="invite-error">
                                {err}
                            </p>
                        )}
                        <button
                            type="submit"
                            className="btn btn-primary w-full"
                            disabled={busy}
                            data-testid="invite-submit"
                        >
                            {busy ? "Saving…" : "Continue"}
                            <ArrowRight size={16} />
                        </button>
                    </form>

                    <div
                        className="mt-10 flex items-start gap-2 text-xs text-muted pt-4"
                        style={{ borderTop: "1px solid var(--brand-surface)" }}
                    >
                        <ShieldCheck size={14} className="mt-0.5" />
                        Your password is hashed with bcrypt. Bank info will be encrypted at rest.
                    </div>
                </div>
            </div>
        </div>
    );
}
